import React, { useMemo } from 'react';
import { RankedCamera } from '../../utils/CameraRankingUtils';
import { getFrameImagePath } from '../../services/colmapLoader';
import { Camera } from '../../types/colmap';
// distance import removed - now using calculateCameraSpaceDepth for scaling
import { calculateScales, calculateCameraSpaceDepth, calculateFocalLength } from '../../utils/ThumbnailScaleCalculator';

interface FrameRendererProps {
  camera: RankedCamera;
  index: number;
  shopId: string;
  currentSectionFramesPath?: string;
  isExpandedState: boolean;
  calculatedThumbnailWidth: number;
  point3D?: [number, number, number];
  allCameras?: Camera[];
  circleRadiusPx?: number;
  tappedFrameIndex?: number;
  projections?: Map<number, [number, number]>;
  playingThumbnailIndex: number | null;
  initialRadiusRef: React.MutableRefObject<number | null>;
  baseGeometryScalesRef: React.MutableRefObject<Map<number, number>>;
  baseCalibrationRef: React.MutableRefObject<number>;
  knobScaleFactorRef: React.MutableRefObject<number>;
  playingThumbnailRef: React.RefObject<HTMLDivElement>;
  onSelect: (cameraId: number, projectedPixel?: [number, number], radiusPxImageSpace?: number, scaleData?: { circleScale: number; distanceScale: number; circleRadiusPx: number; depth: number; focalLength: number }) => void;
  isPlaybackActive: boolean;
  setIsPlaybackActive: (active: boolean) => void;
  playbackIntervalRef: React.MutableRefObject<NodeJS.Timeout | null>;
  setPlayingThumbnailIndex: (index: number | null) => void;
  overrideCamera?: RankedCamera;
}

const FrameRendererComponent: React.FC<FrameRendererProps> = ({
  camera,
  index,
  shopId,
  currentSectionFramesPath,
  isExpandedState,
  calculatedThumbnailWidth: _calculatedThumbnailWidth,
  point3D,
  allCameras,
  circleRadiusPx,
  tappedFrameIndex,
  projections,
  playingThumbnailIndex,
  initialRadiusRef,
  baseGeometryScalesRef,
  baseCalibrationRef,
  knobScaleFactorRef,
  playingThumbnailRef,
  onSelect,
  isPlaybackActive,
  setIsPlaybackActive,
  playbackIntervalRef,
  setPlayingThumbnailIndex,
  overrideCamera,
}) => {
  // Use override camera for display ONLY if it matches this frame's index (playback mode)
  // Memoize to prevent recalculating on every render which causes image reloads
  const displayCamera = useMemo(() => 
    overrideCamera?.frameIndex === camera.frameIndex ? overrideCamera : camera,
    [overrideCamera, camera]
  )
  const imageUrl = useMemo(() => 
    getFrameImagePath(shopId, displayCamera.frameFile, currentSectionFramesPath || undefined),
    [shopId, displayCamera.frameFile, currentSectionFramesPath]
  )
  const aspectRatio = displayCamera.intrinsics.width / displayCamera.intrinsics.height
  
  // Calculate dimensions maintaining aspect ratio
  let thumbnailWidth: number
  let thumbnailHeight: number
  
  if (isExpandedState) {
    // In expanded mode, fit within available space while maintaining aspect ratio
    const availableHeight = window.innerHeight * 0.8 - 74 // calc(80vh - 74px)
    const availableWidth = window.innerWidth * 0.95 - 40   // calc(95vw - 40px)
    
    // Calculate dimensions that fit within constraints while maintaining aspect ratio
    const heightConstrainedWidth = availableHeight * aspectRatio
    const widthConstrainedHeight = availableWidth / aspectRatio
    
    if (heightConstrainedWidth <= availableWidth) {
      // Height constraint is tighter - use full height, calculate width
      thumbnailHeight = availableHeight
      thumbnailWidth = heightConstrainedWidth
    } else {
      // Width constraint is tighter - use full width, calculate height
      thumbnailWidth = availableWidth
      thumbnailHeight = widthConstrainedHeight
    }
    
    thumbnailWidth = Math.round(thumbnailWidth)
    thumbnailHeight = Math.round(thumbnailHeight)
  } else {
    // Normal mode: fixed width, calculated height
    thumbnailWidth = 120
    thumbnailHeight = Math.round(thumbnailWidth / aspectRatio)
  }
  
  // Pre-calculate knob adjustments BEFORE camera processing
  const hasInitialRadius = initialRadiusRef.current !== null
  const radiusChanged = hasInitialRadius && circleRadiusPx !== undefined &&
                       Math.abs(circleRadiusPx - initialRadiusRef.current!) > 0.1
  
  // Update knob factor ONCE before processing cameras
  if (index === 0 && radiusChanged && circleRadiusPx !== undefined) {
    const userAdjustment = initialRadiusRef.current! / circleRadiusPx
    knobScaleFactorRef.current = baseCalibrationRef.current * userAdjustment
  }
  
  // Memoize transform calculation to prevent unnecessary DOM updates during playback
  const imageTransform = useMemo(() => {
    let transform = ''
    
    if (point3D && allCameras && displayCamera.frameIndex < allCameras.length) {
      const cameraData = allCameras[displayCamera.frameIndex]
      const projectedPixel = projections?.get(displayCamera.frameIndex)
      if (cameraData && projectedPixel) {
        let scale: number
        
        // 🧪 TESTING FLAG: Force distance fallback to test knob scale architecture
        const FORCE_DISTANCE_FALLBACK = true
        
        if (circleRadiusPx && !FORCE_DISTANCE_FALLBACK) {
          // Check if this is initial calculation: scales map is empty OR this camera hasn't been calculated yet
          const isInitialPass = baseGeometryScalesRef.current.size === 0
          const needsInitialCalculation = isInitialPass || !baseGeometryScalesRef.current.has(camera.frameIndex)
          
          if (needsInitialCalculation) {
            // ========== INITIAL CALCULATION: Calculate base geometry scale ==========
            // Store the initial radius from first render
            if (!initialRadiusRef.current) {
              initialRadiusRef.current = circleRadiusPx
              // Note: knobScaleFactorRef will be set by calibration logic below
            }
            
            // Calculate 3D radius from selected frame
            let circleRadiusMeters: number | undefined = undefined
            const selectedCameraData = allCameras[tappedFrameIndex!]
            if (selectedCameraData) {
              const depth = calculateCameraSpaceDepth(point3D, selectedCameraData)
              const focalLength = calculateFocalLength(selectedCameraData)
              circleRadiusMeters = (circleRadiusPx * depth) / focalLength
            } else {
              circleRadiusMeters = circleRadiusPx / 10
            }
            
            // Project 3D radius to THIS camera
            const scales = calculateScales(point3D, circleRadiusMeters, thumbnailWidth, cameraData)
            const projectedCircleRadiusPx = scales.circleRadiusPx
            
            // Calculate BASE GEOMETRY scale for THIS camera using IMAGE WIDTH
            const imageWidth = camera.intrinsics.width
            const baseGeometryScale = (imageWidth / 2) / projectedCircleRadiusPx
            
            // Store base geometry scale for this camera
            baseGeometryScalesRef.current.set(camera.frameIndex, baseGeometryScale)
            
            // CALIBRATION: For tapped frame, calculate correction factor
            if (camera.frameIndex === tappedFrameIndex && baseCalibrationRef.current === 1.0) {
              const desiredScale = imageWidth / (2 * circleRadiusPx)
              baseCalibrationRef.current = desiredScale / baseGeometryScale
              knobScaleFactorRef.current = baseCalibrationRef.current
            }
            
            // Compose final scale: baseGeometry × knobFactor
            scale = baseGeometryScale * knobScaleFactorRef.current
          } else if (radiusChanged) {
            // ========== KNOB ADJUSTMENT: Apply pre-calculated knob factor ==========
            // Get stored base geometry scale for this camera
            const baseGeometryScale = baseGeometryScalesRef.current.get(camera.frameIndex)
            
            if (baseGeometryScale !== undefined) {
              // Compose: base geometry × knob factor
              scale = baseGeometryScale * knobScaleFactorRef.current
            } else {
              scale = 1.0 * knobScaleFactorRef.current
            }
          } else {
            // ========== UNCHANGED: Compose stored scales ==========
            const baseGeometryScale = baseGeometryScalesRef.current.get(camera.frameIndex)
            if (baseGeometryScale !== undefined) {
              // Compose: base geometry × knob factor
              scale = baseGeometryScale * knobScaleFactorRef.current
            } else {
              scale = 1.0
            }
          }
        } else {
          // FALLBACK: Depth-based scaling
          const needsInitialCalculation = baseGeometryScalesRef.current.size === 0 || !baseGeometryScalesRef.current.has(camera.frameIndex)
          
          if (needsInitialCalculation) {
            // ========== INITIAL CALCULATION: Depth as base geometry scale ==========
            const depth = calculateCameraSpaceDepth(point3D, cameraData)
            const baseGeometryScale = depth
            baseGeometryScalesRef.current.set(camera.frameIndex, baseGeometryScale)
            
            // Calculate knob calibration factor from tapped frame
            if (!initialRadiusRef.current && circleRadiusPx !== undefined) {
              initialRadiusRef.current = circleRadiusPx
              
              // For tapped frame: Calculate what scale makes circle fill thumbnail width
              const imageWidth = camera.intrinsics.width
              const desiredScale = imageWidth / (2 * circleRadiusPx)
              
              // Calibrate knob: Convert depth units to zoom units
              baseCalibrationRef.current = desiredScale / depth
              knobScaleFactorRef.current = baseCalibrationRef.current
            }
            
            // Compose final scale: baseGeometry × knobFactor
            scale = baseGeometryScale * knobScaleFactorRef.current
          } else if (radiusChanged) {
            // ========== KNOB ADJUSTMENT: Apply pre-calculated knob factor ==========
            const baseGeometryScale = baseGeometryScalesRef.current.get(camera.frameIndex)
            if (baseGeometryScale !== undefined) {
              scale = baseGeometryScale * knobScaleFactorRef.current
            } else {
              const dist = calculateCameraSpaceDepth(point3D, cameraData)
              scale = dist * knobScaleFactorRef.current
            }
          } else {
            // ========== UNCHANGED: Compose stored scales ==========
            const baseGeometryScale = baseGeometryScalesRef.current.get(camera.frameIndex)
            scale = baseGeometryScale !== undefined 
              ? baseGeometryScale * knobScaleFactorRef.current 
              : calculateCameraSpaceDepth(point3D, cameraData)
          }
        }
        
        // Scale projected pixel from full image coords to thumbnail coords
        const scaleX = thumbnailWidth / displayCamera.intrinsics.width
        const scaleY = thumbnailHeight / displayCamera.intrinsics.height
        const pixelInThumbX = projectedPixel[0] * scaleX
        const pixelInThumbY = projectedPixel[1] * scaleY
        
        // Move 2D point to thumbnail center, accounting for scale
        const offsetX = ((thumbnailWidth / 2) - pixelInThumbX) * scale
        const offsetY = ((thumbnailHeight / 2) - pixelInThumbY) * scale
        transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`
      }
    }
    
    return transform
  }, [
    point3D,
    displayCamera.frameIndex,
    displayCamera.intrinsics.width,
    displayCamera.intrinsics.height,
    thumbnailWidth,
    thumbnailHeight,
    allCameras,
    projections,
    circleRadiusPx,
    radiusChanged,
    camera.frameIndex,
    camera.intrinsics.width,
    tappedFrameIndex
  ])
  
  // Store these for click handler to pass to onSelect
  let circleRadiusMeters: number | undefined = undefined
  let projectedCircleRadiusPx: number | undefined = undefined
  
  // Debug marker position (for visualizing projected point on thumbnail)
  let pixelInThumbX = 0
  let pixelInThumbY = 0
  
  // Recalculate debug values if needed (outside memo since they're only used in non-critical rendering)
  if (point3D && allCameras && displayCamera.frameIndex < allCameras.length) {
    const cameraData = allCameras[displayCamera.frameIndex]
    const projectedPixel = projections?.get(displayCamera.frameIndex)
    if (cameraData && projectedPixel) {
      const scaleX = thumbnailWidth / displayCamera.intrinsics.width
      const scaleY = thumbnailHeight / displayCamera.intrinsics.height
      pixelInThumbX = projectedPixel[0] * scaleX
      pixelInThumbY = projectedPixel[1] * scaleY
    }
  }

  
  return (
    <div
      ref={playingThumbnailIndex === camera.frameIndex ? playingThumbnailRef : null}
      key={camera.id}
      className={`camera-thumbnail-item ${playingThumbnailIndex === camera.frameIndex ? 'playing' : ''}`}
      onClick={() => {
        // Stop playback when user manually selects a frame
        if (isPlaybackActive) {
          setIsPlaybackActive(false)
          if (playbackIntervalRef.current) {
            clearInterval(playbackIntervalRef.current)
          }
          
          // Select the CURRENTLY PLAYING frame, not the container's frame
          const frameToSelect = playingThumbnailIndex ?? camera.frameIndex
          console.log(`   ▶️ PLAYBACK MODE - Selecting frame: ${frameToSelect}`)
          const projectedPixel = projections?.get(frameToSelect)
          const worldRadiusToPass = initialRadiusRef.current || circleRadiusPx
          onSelect(frameToSelect, projectedPixel, worldRadiusToPass)
          
          // Scroll to the selected frame's actual thumbnail BEFORE clearing playing state
          console.log(`   📜 SCROLLING to frame ${frameToSelect} thumbnail`)
          setTimeout(() => {
            if (playingThumbnailRef.current) {
              console.log(`   ✅ Scrolling to thumbnail for frame ${frameToSelect}`)
              playingThumbnailRef.current.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'center'
              })
            } else {
              console.log(`   ⚠️ playingThumbnailRef is null, cannot scroll`)
            }
            setPlayingThumbnailIndex(null)
          }, 50)
        } else {
          // Normal click - use camera's actual frame
          console.log(`   ⏸️ NORMAL MODE - Selecting frame: ${camera.frameIndex}`)
          const projectedPixel = projections?.get(camera.frameIndex)
          const worldRadiusToPass = initialRadiusRef.current || circleRadiusPx
          
          // Calculate 3D scale data if we have point3D and circleRadiusMeters
          let scaleData: { circleScale: number; distanceScale: number; circleRadiusPx: number; depth: number; focalLength: number } | undefined
          if (point3D && allCameras && camera.frameIndex < allCameras.length && circleRadiusPx) {
            const cameraData = allCameras[camera.frameIndex]
            // Get circleRadiusMeters from parent state (passed down from initial tap calculation)
            // For now, calculate from current 2D radius as fallback
            const depth = calculateCameraSpaceDepth(point3D, cameraData)
            const focalLength = calculateFocalLength(cameraData)
            const circleRadiusMeters = (circleRadiusPx * depth) / focalLength
            
            const scales = calculateScales(point3D, circleRadiusMeters, _calculatedThumbnailWidth, cameraData)
            scaleData = scales
            console.log(`🔵 [FrameRenderer] Calculated scales for frame ${camera.frameIndex}:`, {
              circleRadiusPx: scales.circleRadiusPx.toFixed(1),
              depth: scales.depth.toFixed(2),
              circleScale: scales.circleScale.toFixed(2)
            })
          }
          
          onSelect(camera.frameIndex, projectedPixel, worldRadiusToPass, scaleData)
        }
      }}
      style={isExpandedState ? {
        width: `${thumbnailWidth}px`,
        height: `${thumbnailHeight}px`,
        minWidth: 'unset'
      } : {
        width: `${thumbnailWidth}px`,
        height: `${thumbnailHeight}px`,
        minWidth: `${thumbnailWidth}px`
      }}
    >
      <div className="camera-thumbnail-label">#{index + 1}: F{camera.frameIndex}</div>
      {projections?.has(camera.frameIndex) && (
        <div 
          className="camera-thumbnail-coords"
          style={{
            opacity: 1,
            transition: 'opacity 0.3s ease-in-out',
            pointerEvents: 'auto'
          }}
        >
          {(() => {
            const pixel = projections.get(camera.frameIndex)
            return `(${Math.round(pixel![0])}, ${Math.round(pixel![1])})`
          })()}
        </div>
      )}
      {point3D && allCameras && camera.frameIndex < allCameras.length && (
        <div 
          className="camera-thumbnail-distance"
          style={{
            opacity: 1,
            transition: 'opacity 0.3s ease-in-out',
            pointerEvents: 'auto'
          }}
        >
          {(() => {
            const cameraData = allCameras[camera.frameIndex]
            if (cameraData) {
              // Use camera space depth (distance along camera's Z-axis) instead of Euclidean distance
              const depth = calculateCameraSpaceDepth(point3D, cameraData)
              return `${depth.toFixed(2)}m`
            }
            return ''
          })()}
        </div>
      )}
      <img
        key={`frame-${camera.frameIndex}`}
        src={imageUrl}
        alt={`Camera ${camera.frameIndex}`}
        className="camera-thumbnail-image"
        style={{ transform: imageTransform || 'none' }}
        onLoad={(e) => {
          // Hide loading spinner when image loads
          const target = e.target as HTMLImageElement
          const spinner = target.parentElement?.querySelector('.camera-thumbnail-loading') as HTMLElement
          if (spinner) spinner.style.display = 'none'
        }}
        onError={(e) => {
          // Hide image and show fallback when image fails
          const target = e.target as HTMLImageElement
          target.style.display = 'none'
          const spinner = target.parentElement?.querySelector('.camera-thumbnail-loading') as HTMLElement
          const fallback = target.parentElement?.querySelector('.camera-thumbnail-fallback') as HTMLElement
          if (spinner) spinner.style.display = 'none'
          if (fallback) fallback.style.display = 'flex'
        }}
      />
      <div className="camera-thumbnail-loading">
        <div className="camera-thumbnail-spinner"></div>
      </div>
      <div className="camera-thumbnail-fallback">
        <span className="camera-thumbnail-number">{camera.frameIndex}</span>
      </div>
    </div>
  )
}

// Wrap in React.memo with custom comparison to prevent re-renders when props haven't meaningfully changed
export const FrameRenderer = React.memo(FrameRendererComponent, (prevProps, nextProps) => {
  // Return true to skip re-render, false to re-render
  // CRITICAL: Ignore refs, functions, Maps - these are always new references but don't affect output
  
  // 1. Camera identity changed (different frame)
  if (prevProps.camera.frameIndex !== nextProps.camera.frameIndex) {
    return false
  }
  
  // 2. Playing state changed (THIS specific frame became/stopped playing)
  // Check both override camera and playingThumbnailIndex to determine playing state
  const wasPlayingViaOverride = prevProps.overrideCamera?.frameIndex === prevProps.camera.frameIndex
  const isPlayingViaOverride = nextProps.overrideCamera?.frameIndex === nextProps.camera.frameIndex
  const wasPlayingViaIndex = prevProps.playingThumbnailIndex === prevProps.camera.frameIndex
  const isPlayingViaIndex = nextProps.playingThumbnailIndex === nextProps.camera.frameIndex
  const wasPlaying = wasPlayingViaOverride || wasPlayingViaIndex
  const isPlaying = isPlayingViaOverride || isPlayingViaIndex
  if (wasPlaying !== isPlaying) {
    return false
  }
  
  // 3. Expanded state toggled
  if (prevProps.isExpandedState !== nextProps.isExpandedState) {
    return false
  }
  
  // 5. Calculated width changed (layout change)
  if (prevProps.calculatedThumbnailWidth !== nextProps.calculatedThumbnailWidth) {
    return false
  }
  
  // 6. Circle radius changed (affects transform calculations)
  if (prevProps.circleRadiusPx !== nextProps.circleRadiusPx) {
    return false
  }
  
  // All critical props unchanged - skip re-render
  return true
})
